// 📄 pages/Credenciales/SolicitudesCredenciales.tsx
// "Solicitudes de credenciales" (menú del usuario; los correos llevan acá —
// docs/plans/plan-revelado-credenciales.md, R3):
//   - Por aprobar: pendientes donde soy aprobador y todavía no decidí.
//   - Mis solicitudes: lo que pedí; una aprobada se ve desde acá (15 min, una vez).
//   - Historial de la organización activa: solo owner/admin (R4).
import React, { useCallback, useEffect, useState } from 'react';
import { KeyRound, RefreshCw, Check, X, Clock, Inbox } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import {
  EstadoSolicitud,
  SolicitudCredencial,
  listarSolicitudes,
  decidirSolicitud,
  cancelarSolicitud,
  revelarConSolicitud
} from '../../services/credencialAccesoService.ts';
import { FlujoRevelar } from '../../components/credenciales/ObtenerCredencial.tsx';
import { HistorialOrganizacion } from '../../components/credenciales/HistorialCredencial.tsx';

type Pestaña = 'porAprobar' | 'mias' | 'historial';

const ESTADOS: Record<EstadoSolicitud, { label: string; clase: string }> = {
  pendiente: { label: 'Pendiente', clase: 'bg-amber-500/15 text-amber-300' },
  aprobada: { label: 'Aprobada', clase: 'bg-emerald-500/15 text-emerald-400' },
  rechazada: { label: 'Rechazada', clase: 'bg-red-500/15 text-red-400' },
  cancelada: { label: 'Cancelada', clase: 'bg-white/10 text-gray-400' },
  expirada: { label: 'Vencida', clase: 'bg-white/10 text-gray-400' },
  revelada: { label: 'Vista', clase: 'bg-blue-500/15 text-blue-400' }
};

const fecha = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '');

export default function SolicitudesCredenciales() {
  const { orgActual } = useAuth();
  const esGestorOrg = orgActual?.role === 'owner' || orgActual?.role === 'admin';
  const [pestaña, setPestaña] = useState<Pestaña>('porAprobar');
  const [solicitudes, setSolicitudes] = useState<SolicitudCredencial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState<string | null>(null);
  const [comentarios, setComentarios] = useState<Record<string, string>>({});
  const [revelando, setRevelando] = useState<SolicitudCredencial | null>(null);

  const cargar = useCallback(() => {
    if (pestaña === 'historial') return Promise.resolve();
    setLoading(true);
    setError(null);
    return listarSolicitudes(pestaña)
      .then(setSolicitudes)
      .catch((err) => setError(err.message || 'No se pudieron cargar las solicitudes.'))
      .finally(() => setLoading(false));
  }, [pestaña]);

  useEffect(() => { cargar(); }, [cargar]);

  const ejecutar = async (id: string, accion: () => Promise<unknown>) => {
    setTrabajando(id);
    setError(null);
    try {
      await accion();
      await cargar();
    } catch (err: any) {
      setError(err.message || 'No se pudo completar la acción.');
    } finally {
      setTrabajando(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-white flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-blue-400" /> Solicitudes de credenciales
        </h1>
        <button type="button" onClick={cargar} title="Recargar" className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <p className="text-sm text-gray-400">
        Pedidos para ver contraseñas guardadas (PUser de CIG, SUNAT, APIs, Vault). Nadie aprueba su propia solicitud, y todo queda en el historial de la credencial.
      </p>

      <div className="flex gap-1 border-b border-white/10">
        {([['porAprobar', 'Por aprobar'], ['mias', 'Mis solicitudes'], ...(esGestorOrg ? [['historial', 'Historial de la organización']] : [])] as [Pestaña, string][]).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setPestaña(id)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${pestaña === id ? 'border-blue-500 text-white' : 'border-transparent text-gray-400 hover:text-white'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

      {pestaña === 'historial' && orgActual ? (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-2">
          <p className="text-xs text-gray-500">Todo lo que pasó con las credenciales de {orgActual.name}: solicitudes, aprobaciones, quién vio cada contraseña, cambios de aprobadores y permisos.</p>
          <HistorialOrganizacion orgId={orgActual.id} />
        </div>
      ) : loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
          <p className="text-sm">Cargando solicitudes...</p>
        </div>
      ) : solicitudes.length === 0 ? (
        <div className="text-center py-12 bg-white/5 border border-white/10 rounded-2xl text-gray-500">
          <Inbox className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">{pestaña === 'porAprobar' ? 'No tienes solicitudes por aprobar.' : 'Todavía no pediste ninguna credencial.'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {solicitudes.map((s) => {
            const estado = ESTADOS[s.estado] || ESTADOS.pendiente;
            const ocupado = trabajando === s.id;
            return (
              <div key={s.id} className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-white truncate" title={s.etiqueta}>{s.etiqueta}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {pestaña === 'porAprobar' ? `Pide: ${s.solicitante.nombre || s.solicitante.uid} · ` : ''}{fecha(s.creadaEn)}
                    </p>
                  </div>
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${estado.clase}`}>{estado.label}</span>
                </div>

                <p className="text-sm text-gray-300 bg-black/20 border border-white/10 rounded-lg p-3">{s.motivo}</p>

                <p className="text-xs text-gray-500">
                  {s.modo === 'todos' ? 'Tienen que aprobar todos los aprobadores' : 'Basta con que apruebe uno'}
                  {s.decisiones.length > 0 && ` · ${s.decisiones.map((d) => `${d.nombre || d.uid} ${d.decision === 'aprobar' ? 'aprobó' : 'rechazó'}${d.comentario ? ` ("${d.comentario}")` : ''}`).join(' · ')}`}
                </p>

                {pestaña === 'porAprobar' && s.estado === 'pendiente' && (
                  <div className="space-y-2 pt-1">
                    <input
                      type="text"
                      maxLength={500}
                      value={comentarios[s.id] || ''}
                      onChange={(e) => setComentarios((prev) => ({ ...prev, [s.id]: e.target.value }))}
                      placeholder="Comentario (opcional)"
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2 text-sm text-white focus:outline-none focus:border-blue-500"
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        disabled={ocupado}
                        onClick={() => ejecutar(s.id, () => decidirSolicitud(s.id, 'rechazar', comentarios[s.id]))}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-red-400 hover:bg-red-500/10 disabled:opacity-40"
                      >
                        <X className="w-3.5 h-3.5" /> Rechazar
                      </button>
                      <button
                        type="button"
                        disabled={ocupado}
                        onClick={() => ejecutar(s.id, () => decidirSolicitud(s.id, 'aprobar', comentarios[s.id]))}
                        className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 px-3 py-1.5 rounded-lg text-xs font-semibold text-[#fff] disabled:opacity-40"
                      >
                        {ocupado ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Aprobar
                      </button>
                    </div>
                  </div>
                )}

                {pestaña === 'mias' && (s.estado === 'pendiente' || s.estado === 'aprobada') && (
                  <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
                    {s.estado === 'aprobada' && (
                      <span className="text-xs text-gray-400 flex items-center gap-1.5 mr-auto">
                        <Clock className="w-3.5 h-3.5" /> Disponible hasta {fecha(s.expiraEn)}, una sola vez.
                      </span>
                    )}
                    <button
                      type="button"
                      disabled={ocupado}
                      onClick={() => ejecutar(s.id, () => cancelarSolicitud(s.id))}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-400 hover:text-white hover:bg-white/10 disabled:opacity-40"
                    >
                      <X className="w-3.5 h-3.5" /> Cancelar
                    </button>
                    {s.estado === 'aprobada' && (
                      <button
                        type="button"
                        onClick={() => setRevelando(s)}
                        className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 px-3 py-1.5 rounded-lg text-xs font-semibold text-[#fff]"
                      >
                        <KeyRound className="w-3.5 h-3.5" /> Ver contraseña
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {revelando && (
        <FlujoRevelar
          revelar={() => revelarConSolicitud(revelando.id)}
          onClose={() => { setRevelando(null); cargar(); }}
        />
      )}
    </div>
  );
}
